/**
 * SPEC.md §7 — M6.3/M6.4 (issues #30, #31): "Precedence on load: URL hash →
 * plotter:draft → the flowchart template," plus the shelve invariant when
 * a hash overrides an existing draft. `plotter:draft` carries an id and an
 * `originalCode` baseline (M6.4) — not just a plain source string — so the
 * same buffer keeps one consistent identity across a reload, and
 * history.ts's `shouldShelve` can tell an edited draft from an untouched
 * one even after the page that first loaded it is long gone.
 */
import { decodeShareState } from './share';
import { readStorage } from './storage';

export const DRAFT_STORAGE_KEY = 'plotter:draft';

export interface DraftBuffer {
  id: string;
  code: string;
  /** The code this buffer had when it was loaded — compared against `code` to tell an edited draft from an untouched one (history.ts's `shouldShelve`). */
  originalCode: string;
}

export type InitialSourceDecision =
  | { from: 'hash'; code: string; diagramTheme: string; look: string; outgoingDraft: DraftBuffer | null }
  | { from: 'draft'; code: string; id: string; originalCode: string }
  | { from: 'template'; code: string };

/**
 * Pure — no `window`/`localStorage` access — so it's unit-testable
 * independent of `getInitialSource`'s thin impure wrapper below. When the
 * hash wins over an existing draft, `outgoingDraft` carries that draft back
 * to the caller (App.tsx) so it can be shelved — SPEC.md §7's shelve
 * invariant applies here too: "clicking a colleague's permalink destroys
 * an afternoon of unexported work" is exactly what this prevents. This
 * module never writes to `plotter:history` itself — that stays history.ts's
 * job, called from App.tsx once it has this decision in hand.
 *
 * `diagramTheme`/`look` on the 'hash' case are `decoded.diagramTheme`/
 * `decoded.look` verbatim — still raw strings, not narrowed to
 * `mermaidRender.ts`'s `DiagramTheme`/`Look` unions. Validating a decoded
 * hash's values is a rendering-layer concern, not this module's, so that
 * narrowing (and its default fallback) happens where App.tsx applies them,
 * keeping this module free of a dependency on mermaidRender.ts.
 */
export function resolveInitialSource(hashFragment: string, draft: DraftBuffer | null, fallback: string): InitialSourceDecision {
  const decoded = decodeShareState(hashFragment);
  if (decoded !== null) {
    return { from: 'hash', code: decoded.code, diagramTheme: decoded.diagramTheme, look: decoded.look, outgoingDraft: draft };
  }
  if (draft !== null) return { from: 'draft', code: draft.code, id: draft.id, originalCode: draft.originalCode };
  return { from: 'template', code: fallback };
}

function isDraftBuffer(value: unknown): value is DraftBuffer {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.code === 'string' && typeof v.originalCode === 'string';
}

/** Corrupt or foreign `plotter:draft` content degrades to "no draft," not a crash. */
function parseDraftBuffer(raw: string | null): DraftBuffer | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isDraftBuffer(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Reads the real hash and draft from the browser, then applies the precedence above. */
export function getInitialSource(fallback: string): InitialSourceDecision {
  const draft = parseDraftBuffer(readStorage(DRAFT_STORAGE_KEY));
  return resolveInitialSource(window.location.hash.slice(1), draft, fallback);
}
